import { LocalStorage } from '../../local-storage/startcodes-storage';
import { Action } from '@visikon/core-models/content';
import { fetchGet, fetchPost } from '@visikon/utils/src/utils';
import { CustomError } from 'ts-custom-error';
import { TextKeys } from '@visikon/core-models/i18n/translations';
import { logger } from '@visikon/utils/src/logger';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useAuthenticateByCode } from '../../api/auth-api';
import { setProgramActions } from '../../actions/resourcesActions';
import { HttpError } from '../../api/query-utils';
import { LoginResponse } from '../../api/auth-api.models';
import { setStartCode } from '../../actions/authActions';
import { useHistory } from 'react-router-dom';

export type ProgramActions = {
  _id: string;
  actions?: Action[];
};

export class CreateStartCodeUserError extends CustomError {
  constructor(public readonly i18n_error: TextKeys) {
    super();
  }
}

export type StartCodeUserCreated = { code: string; programActions: ProgramActions };
async function GetAccessCodeFromStartCode(startCode: string): Promise<StartCodeUserCreated> {
  const savedAccessCode = await LocalStorage.getAccessCode(startCode);
  if (savedAccessCode) {
    // Use destructuring because the '/programinfo/:startCode' endpoint returns more fields
    // then we need for the refactored and simplified setup for program actions
    const { _id, actions }: ProgramActions = await fetchGet(`programInfo/${startCode}`);
    return { code: savedAccessCode, programActions: { _id, actions } };
  }

  const result = await fetchPost(`users/createStartCodeUser`, {}, { startCode });
  if (!result.ok) {
    if (result.status === 404) throw new CreateStartCodeUserError('merge_error_unknown_code');
    throw new Error(result.statusText, { cause: result });
  }

  return result.json() as Promise<StartCodeUserCreated>;
}

export function useAuthenticateByStartCode() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { mutateAsync: authenticate } = useAuthenticateByCode();

  return useMutation<LoginResponse & StartCodeUserCreated, Error | HttpError | CreateStartCodeUserError, string>({
    mutationFn: async (startCode) => {
      const startCodeUser = await GetAccessCodeFromStartCode(startCode);

      const loginResponse = await authenticate({ code: startCodeUser.code });

      return { ...loginResponse, ...startCodeUser };
    },
    onSuccess: (data, startCode) => {
      if (!!data.programActions) {
        dispatch(setProgramActions({ programActions: data.programActions, history }));
      }

      dispatch(setStartCode(startCode));

      LocalStorage.saveAccessCode({ startCode, accessCode: data.code }).catch(() => {
        logger.error('Failed to save generated access code for start code ' + startCode);
      });
    },
    useErrorBoundary: true,
    retry: false,
  });
}
